import { store } from 'src/store'
import { Platform, colors, scroll, Notify } from 'quasar'
import Dialog from './dialog'
import sanitizeHtml from 'sanitize-html'

import { markRaw } from 'vue'
import { baseUrl } from 'src/router'
const { brightness, lighten, hsvToRgb, rgbToHsv, textToRgb, rgbToHex } = colors

export default {
  data () {
    return {
      baseUrl: baseUrl(),
      siteName: process.env.SITE_NAME,
      permissions: markRaw(process.env.PERMISSIONS),
      newbies: markRaw(process.env.NEWBIES),
      helpCodes: markRaw(process.env.HELP_CODES)
    }
  },
  computed: {
    windowWidth () {
      return this.$q.screen.width
    },
    windowHeight () {
      return this.$q.screen.height
    },
    company () {
      return this.$store.getters['company/getCompany']
    },
    userData () {
      return this.$store.getters.getUserData
    },
    isLoggedIn () {
      return this.$store.getters.isLoggedIn
    }
  },
  methods: {
    /**
     * go to a specific page on the portal
     * @param url
     * @param params
     * @param timeout
     * @param replace
     */
    goTo: function (url, params = {}, timeout = false, replace = false) {
      const go = () => {
        const method = replace ? 'replace' : 'push'
        const path = isStringType(url) ? (this.baseUrl + url) : this.$route.path
        // If query params are embedded into the URL, push directly
        if (path.includes('?')) {
          this.$router[method](path)
        }
        // Else split into object
        else {
          this.$router[method]({
            path,
            query: params
          })
        }
      }

      if (timeout) {
        setTimeout(() => {
          go()
        }, 250)
      }
      else {
        go()
      }
    },
    goToHref (url, systemWindow = false, skipHrefCheck = false, options = undefined) {
      if (!skipHrefCheck && !url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'http://' + url
      }
      if (Platform.is.nativeMobile) {
        options = 'location=yes,toolbarposition=top,allowInlineMediaPlayback=yes'
      }
      const popup = window.open(
        url,
        isStringType(systemWindow) ? systemWindow : (systemWindow || !Platform.is.nativeMobile ? '_system' : '_blank'),
        options
      )
      if (!Platform.is.nativeMobile && (!popup || popup.closed || isUndefinedType(popup.closed))) {
        Dialog({
          title: 'Popup Blocked',
          message: 'Popups have been blocked. Please update your browser site settings to allow popups from ' + process.env.SITE_NAME
        })
      }
    },
    // now identical to goTo(). Use for separation
    goToAuth (...params) {
      this.goTo(...params)
    },
    goToAddress (address) {
      this.goToHref('http://maps.google.com/?q=' + address, true)
    },
    goToPhone (phone) {
      // remove spaces from string - phone number should have no spaces
      window.location.href = 'tel://' + (phone || '').replace(/\s/g, '')
    },
    goToEmail (email) {
      window.location.href = 'mailto:' + email
    },
    // Go back if ?back=true exists, else use a default callback
    goBackIfQueryParam (ifNotCallback = null) {
      if (this.$route.query.back && this.$route.query.back === 'true' && window.history.length > -1) {
        window.history.back()
      }
      else if (ifNotCallback) {
        ifNotCallback()
      }
    },
    goBackHandler (mode, defaultRoute = null, ...opts) {
      // values are string within query param URL
      if (!this.$route.query.back || this.$route.query.back === 'true') {
        const trueModeCheck = () => mode === true && defaultRoute && window.history.length > -1
        const partialModeCheck = () => {
          if (!isStringType(mode)) {
            return false
          }

          const fullMatch = opts[0] ?? false
          return this.doesReferrerContain(mode, fullMatch)
        }

        if (trueModeCheck() || partialModeCheck()) {
          window.history.back()
          return
        }
      }

      const route = defaultRoute || (isStringType(mode) ? mode : null)
      if (route !== null) {
        this.goTo(route)
      }
    },
    // Simple setTimeout which can be used within templates
    timed (callback, timeout = 0) {
      return setTimeout(callback, timeout)
    },
    /**
     * Check if the previous URL contains part of a string
     * @param str
     * @param fullMatch
     * @returns {boolean}
     */
    doesReferrerContain (str, fullMatch = false) {
      const lastRoute = this.$router.options.history.state.back || ''
      if (fullMatch) {
        return lastRoute === this.baseUrl + str
      }
      return lastRoute.includes(str)
    },
    can (...perm) {
      for (const ele of perm) {
        if (!this.$store.getters.hasPermission(ele)) {
          return false
        }
      }
      return true
    },
    /**
     * Check if the errors object contains a key
     * If the string contains *, it will look for anything LIKE the key from start of the string up to that point
     * @param obj
     * @param keys
     * @param returnFullKey :If false, full key will be returned, else just the concat potion after *
     * @param returnOnMatch :Return on the first match which is found
     * @returns {boolean}
     */
    getErrorsForProp (obj, keys = [], returnFullKey = false, returnOnMatch = false) {
      const errors = {}
      if (!Array.isArray(keys)) {
        keys = [keys]
      }

      for (const ele of keys) {
        let partialMatch = false
        let checkStr = ele
        if (ele.endsWith('*')) {
          partialMatch = true
          checkStr = ele.split('*')[0]
        }

        // If not partial matching this key, check object exists
        if (!partialMatch && !!obj[checkStr]) {
          errors[ele] = obj[ele]
          if (returnOnMatch) {
            return errors
          }
        }
        // If performing a partial match, check that each obj key starts with the partial matching string
        // And if not returning the full key, split the key and return pieces after the match
        // EG: 'clients.*.description' (match by 'clients.*', return key as 'description')
        else if (partialMatch) {
          for (const errKey in obj) {
            if (errKey.indexOf(checkStr) === 0) {
              let appendErrKey = errKey
              if (!returnFullKey) {
                appendErrKey = errKey.split(checkStr)[1]
              }
              errors[appendErrKey] = obj[errKey]
              if (returnOnMatch) {
                return errors
              }
            }
          }
        }
      }
      return errors
    },
    /* Perform same as above, but only return true if a result is found */
    checkErrorHasProp (obj, keys = []) {
      return Object.keys(this.getErrorsForProp(obj, keys, true, true)).length > 0
    },
    randomString (length = 8) {
      return Math.random().toString(36).slice(length * -1)
    },
    checkValidHexColor (hex) {
      return /^#(?:[0-9a-fA-F]{3}){1,2}$/i.test(hex)
    },
    checkBrightnessOfHex (hex) {
      if (!hex) {
        hex = '#ffffff'
      }
      return brightness(hex)
    },
    /**
     * Change brightness of hex
     * @param hex
     * @param amount (percent)
     * @returns {string}
     */
    changeColorBrightness (hex, amount = 0) {
      if (!hex) {
        hex = '#ffffff'
      }
      return lighten(hex, amount)
    },
    /**
     * Return readable text color for a profile (such as user acc)
     * @param hex
     * @param shiftAwayFromBackground
     * @param backgroundIsLight
     * @param shiftDistance
     * @returns {string|*}
     */
    readableProfileColor (hex, shiftAwayFromBackground = true, backgroundIsLight = true, shiftDistance = 60) {
      // if not supplied, use white colour
      if (!hex) hex = '#ffffff'

      // get current brightness
      const currentBrightness = this.checkBrightnessOfHex(hex)

      // are we going for a light or dark background
      const isBeyondThreshold = backgroundIsLight ? currentBrightness > 170 : currentBrightness < 95

      // change the brightness away from supplied bg target, by supplied amount
      return isBeyondThreshold ? this.changeColorBrightness(hex, shiftAwayFromBackground ? -shiftDistance : shiftDistance) : hex
    },
    /* Get the specific week number for a date */
    weekInMonth (luxonInstance) {
      if (!luxonInstance) {
        return 1
      }

      const firstDayOfMonth = luxonInstance.startOf('month')
      const firstDayOfWeek = firstDayOfMonth.startOf('week')
      const offset = firstDayOfMonth.diff(firstDayOfWeek, 'days').toObject().days

      return Math.ceil((luxonInstance.day + offset) / 7)
    },
    randomColor,
    walkAroundColorWheel,
    randId,
    truncateString,
    pluraliseString,
    minutesToStr,
    friendlyNumber,
    commaNumber,
    stripHtml,
    groupBy,
    checkEmailValid,
    checkMobilePhoneValid,
    trimStringWhitespace,
    scrollToAnchor,
    scrollToFirstError,
    orderObject,
    copyToClipboard
  }
}

function S4 () {
  return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1)
}

/**
 * Trim whitespace from the start and end of a string
 * @param str
 * @returns {null|*}
 */
export function trimStringWhitespace (str) {
  if (!str) {
    return null
  }
  return str.trim()
}

/**
 * Pluralise a string with "s" if length != 0 (eg: 1 client, 2 clients)
 * @param str
 * @param num
 * @param suffix
 * @returns {string}
 */
export function pluraliseString (str, num = 0, suffix = 's') {
  return `${str}${num === 1 ? '' : suffix}`
}

/**
 * Truncate specified string if it is longer than length X (will append ...)
 * @param str
 * @param length
 * @returns {string}
 */
export function truncateString (str = '', length = 20) {
  return `${str.substring(0, length)}${str.length > length ? '...' : ''}`
}

/**
 * Convert minutes to a readable string
 * @param minutesBefore
 * @param nullText
 * @param zeroText
 * @param suffixLabel
 * @returns {string}
 */
export function minutesToStr (minutesBefore = null, nullText = 'No specified time', zeroText = 'At time of appointment', suffixLabel = null) {
  if (minutesBefore === null) {
    return nullText
  }

  let temp = Math.abs(minutesBefore)

  const units = {
    'year': 24 * 60 * 365,
    'month': 24 * 60 * 30,
    'week': 24 * 60 * 7,
    'day': 24 * 60,
    'hour': 60,
    'minute': 1
  }

  const result = []

  for (const name in units) {
    const p = Math.floor(temp / units[name])
    if (p === 1) result.push(' ' + p + ' ' + name)
    if (p >= 2) result.push(' ' + p + ' ' + name + 's')
    temp %= units[name]
  }

  if (result.length > 0) {
    return result.toString() + ' ' + (suffixLabel || '')
  }

  return zeroText
}

/**
 * Group an array by the specified method
 * @param x: array to order
 * @param f: function for ordering the items
 * @returns {*}
 */
export function groupBy (x, f) {
  // eslint-disable-next-line no-sequences
  return x.reduce((a, b) => ((a[f(b)] ||= []).push(b), a), {})
}

/**
 * Parse a string in readable format (eg: 10000 = 10,000)
 * @param str: string to parse as number
 * @param currency: display as currency ($)
 * @param digits: number of decimal places
 * @returns {string}
 */
export function friendlyNumber (str, currency = false, digits = 2) {
  // If currency, don't round up past the supplied digits
  if (currency && digits > 1) {
    const regex = new RegExp(`^-?\\d+(?:\\.\\d{0,${digits}})?`)
    str = (str || 0).toString().match(regex)[0]
  }
  return Number(str).toLocaleString('en-AU', {
    style: currency ? 'currency' : undefined,
    currency: currency ? 'AUD' : undefined,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })
}

/**
 * Perform friendlyNumber for basic number (convert 69420 => 69,420) [nice]
 * @param str
 * @returns {string}
 */
export function commaNumber (str) {
  return friendlyNumber(str, false, 0)
}

/**
 * Strip all HTML from a string
 * @param str
 * @param fixAmpersands
 * @returns {string}
 */
export function stripHtml (str = '', fixAmpersands = false) {
  let sanitisedStr = sanitizeHtml(str, {
    allowedTags: [],
    allowedAttributes: {}
  })
  if (fixAmpersands) {
    sanitisedStr = sanitisedStr.replaceAll('&amp;', '&')
  }
  return sanitisedStr
}

export function makeGuid () {
  if (store.getters.getGuid !== null) {
    return store.getters.getGuid
  }

  const str = randId()
  store.dispatch('guid', {
    guid: str
  })
  return str
}

export function randId () {
  return (S4() + S4() + '-' + S4() + '-4' + S4().substr(0, 3) + '-' + S4() + '-' + S4() + S4() + S4()).toLowerCase()
}

/**
 * Test that an email follows typical syntax
 * @param email
 * @returns {boolean}
 */
export function checkEmailValid (email) {
  if (!email) {
    return false
  }
  return /^\S+@\S+$/i.test(email)
}

export function checkMobilePhoneValid (phone) {
  if (!phone) {
    return false
  }
  return /^(\+\d{1,3}[- ]?)?\d{10}$/.test(phone.replace(/\s/g, ''))
}

// Create valid reference number from string
export function convertToReferenceNumber (str, maxLength = null) {
  if (!str) {
    return null
  }
  str = str.replace(/([^a-zA-Z0-9#_-]+)/g, '')
  if (maxLength) {
    str = str.slice(0, maxLength)
  }
  return str
}

export function scrollToAnchor (anchor, offset = 0, delay = 0, afterDelay = () => {}) {
  setTimeout(() => {
    const ele = document.getElementById(anchor)
    if (ele) {
      const y = ele.getBoundingClientRect().top + window.pageYOffset - offset
      const scrollTarget = scroll.getScrollTarget(ele)
      if (scrollTarget) {
        scroll.setVerticalScrollPosition(scrollTarget, y, 750)
      }
    }
    afterDelay()
  }, delay)
}

/**
 * Scroll to the first Quasar field with a validation error.
 * Walks up from the error field to find a preceding q-item-label (section heading)
 * and scrolls that into view. Falls back to the error field itself.
 * @param {HTMLElement} container - The container element to search for errors within.
 *   Handles Vue 3 fragments where this.$el may be a text node.
 */
export function scrollToFirstError (container) {
  if (!container) return
  // Handle Vue 3 fragments where $el is a text/comment node
  let searchRoot = container
  if (!container.querySelector) {
    searchRoot = container.parentElement
  }
  if (!searchRoot) return
  const errorInput = searchRoot.querySelector('.q-field--error')
  if (!errorInput) return

  // Walk up to find the nearest preceding q-item-label (section heading)
  let target = errorInput
  let el = errorInput
  while (el && el !== searchRoot) {
    if (el.previousElementSibling?.classList?.contains('q-item-label')) {
      target = el.previousElementSibling
      break
    }
    el = el.parentElement
  }

  // Find the scrollable container: dialog card, layout container, or Quasar scroll target
  const dialogCard = target.closest('.content-card')
  let scrollTarget
  let stickyOffset = 0

  if (dialogCard && dialogCard.scrollHeight > dialogCard.clientHeight) {
    scrollTarget = dialogCard
    const toolbar = dialogCard.querySelector('.content-wrapper > div:first-child')
    stickyOffset = toolbar ? toolbar.offsetHeight : 0
  }
  else {
    scrollTarget = scroll.getScrollTarget(target)
    const headerEl = scrollTarget?.closest?.('.q-layout-container')?.querySelector('.q-header') ||
      document.querySelector('.q-header')
    stickyOffset = headerEl ? headerEl.offsetHeight : 0
  }

  if (scrollTarget) {
    const targetTop = target.getBoundingClientRect().top -
      scrollTarget.getBoundingClientRect().top +
      scrollTarget.scrollTop - stickyOffset - 40
    scroll.setVerticalScrollPosition(scrollTarget, Math.max(0, targetTop), 300)
  }
}

/**
 * Generate a random color from 0-255 rgb
 * @param min
 * @param max
 * @returns {string}
 */
export function randomColor (min = 0, max = 255) {
  const randomNum = () => Math.floor(Math.random() * (max - min + 1) + min)
  return `rgb(${randomNum()}, ${randomNum()}, ${randomNum()})`
}

/**
 * Convert a hex color to HSV and change its color on the color wheel
 * @param fromHex
 * @param amount
 * @returns {string}
 */
export function walkAroundColorWheel (fromHex, amount) {
  const fromRgb = textToRgb(fromHex)
  const fromHsv = rgbToHsv(fromRgb)
  fromHsv.h = fromHsv.h + (amount % 360)
  return rgbToHex(hsvToRgb(fromHsv))
}

/**
 * Make a DOM element blink
 * @param node
 * @param classNames
 * @param blinkTime
 */
export function blink (node, classNames, blinkTime = 1000) {
  if (!node || node.classList.contains('blink')) {
    return
  }

  if (!Array.isArray(classNames)) {
    classNames = [classNames]
  }

  node.classList.add(...classNames, 'blink')

  setTimeout(() => {
    node.classList.remove(...classNames)
    // Takes 250ms for animation to complete (.blink css class)
    setTimeout(() => {
      node.classList.remove('blink')
    }, 250)
  }, blinkTime)
}

/**
 * Find a parent vue component by its name
 * @param instance
 * @param componentName
 * @returns {null}
 */
export function findParentComponent (instance, componentName) {
  let parent = instance.$parent
  while (parent) {
    // Make sure the parent component has a specified name attribute
    if (parent.$options.name === componentName) {
      return parent
    }
    parent = parent.$parent
  }
  return null
}

export function orderObject (obj) {
  // eslint-disable-next-line no-sequences,no-return-assign
  return Object.entries(obj).sort().reduce((o, [k, v]) => (o[k] = v, o), {})
}

export function copyToClipboard (value) {
  try {
    const input = document.createElement('textarea')
    document.body.appendChild(input)
    // Strip HTML, and replace <br> with new line chars
    input.value = stripHtml(value.replace(/<br\s*[/]?>/gi, '\n'))
    input.focus()
    input.select()
    document.execCommand('Copy')
    input.remove()

    Notify.create({
      color: 'green',
      message: 'Text copied to clipboard'
    })
  }
  catch (err) {
    Notify.create({
      message: 'Failed to copy to clipboard',
      color: 'red'
    })
  }
}

// Convert a string to object syntax for Vue class bindings
export function strToObj (val, obj = {}) {
  if (!val) {
    val = {}
  }

  if (isStringType(val)) {
    val = { [val]: true }
  }

  return {
    ...obj,
    ...val
  }
}

// Concat a bunch of strings by a separator
// The method can accept conditionals (example: isArchived && 'Archived' - this will return the string if true,
//  otherwise it returns false. False will be cancelled out
export function concatStrIf (separator, ...strs) {
  return strs.filter(item => isStringType(item)).join(separator)
}

// Spread two objects and ignore any "undefined" source values
//  such as { key: undefined }
// Fork from https://stackoverflow.com/questions/39513815/is-there-a-way-to-create-an-object-using-variables-and-ignore-undefined-variable
export function assignDefined (target, ...sources) {
  for (const source of sources) {
    for (const key of Object.keys(source)) {
      const val = source[key]
      if (val !== undefined) {
        target[key] = val
      }
    }
  }
  return target
}

// Bind an additional param to the specified URL
// Will automatically check if ? exists in URL
export function bindParamToUrl (url, value) {
  if (url.match(/\?/)) {
    url += '&'
  }
  else {
    url += '?'
  }
  url += value

  return url
}

// Check if two arrays have the same length and contents
export function doArraysMatch (arr1, arr2, inOrder = false) {
  if (arr1.length !== arr2.length) {
    return false
  }
  const inOrderFunc = (value, index) => value === arr2[index]
  const anywhereFunc = value => arr2.includes(value)
  return arr1.every(inOrder ? inOrderFunc : anywhereFunc)
}

// convert array to set and back again to force unique values
export function uniqueArray (arr) {
  return Array.from(new Set(arr))
}

// merge two arrays together, optionally stripping duplicate entries
export function mergeArrays (arr1, arr2, unique = true) {
  const merged = arr1.concat(arr2)
  return unique ? uniqueArray(merged) : merged
}

// ------------------------------------
// global type checking helpers:
// ------------------------------------
//
// string
export function isStringType (val) {
  return typeof val === 'string' || val instanceof String
}
// number
export function isNumberType (val) {
  return typeof val === 'number' || val instanceof Number
}
// undefined
export function isUndefinedType (val) {
  return typeof val === 'undefined'
}

// Create an object keyed by repeating array key
export function keyBy (array, key) {
  const obj = {}
  for (const item of array) {
    obj[item[key]] = item
  }
  return obj
}

// Sanitise HTML which may be injected directly into the DOM
export function sanitizeHtmlAttribute (string) {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '/': '&#x2F;',
    '`': '&#x60;',
    '=': '&#x3D;'
  }
  const reg = /[&<>"'/]/ig
  return string.replace(reg, (match) => (map[match]))
}
